import { defineMessages } from 'react-intl';
import type { IntlShape } from 'react-intl';

import { SOCIAL_LABELS } from 'volto-ministerio-publico-federal/components/Icons/social';

import { PLATAFORMAS, RADIO_TITULO } from './helpers';

const messages = defineMessages({
  titleBlock: { id: 'mpf_radio_block', defaultMessage: 'Rádio e podcast' },
  titulo: { id: 'mpf_radio_titulo', defaultMessage: 'Título da faixa' },
  tituloHelp: {
    id: 'mpf_radio_titulo_help',
    defaultMessage: 'Em branco, usa "{padrao}".',
  },
  exibirTitulo: {
    id: 'mpf_radio_exibir_titulo',
    defaultMessage: 'Exibir o título',
  },
  exibirTituloHelp: {
    id: 'mpf_radio_exibir_titulo_help',
    defaultMessage:
      'Ligue quando a faixa aparecer sozinha. Abaixo da faixa de vídeos, o cabeçalho dela já serve às duas.',
  },
  feed: { id: 'mpf_radio_feed', defaultMessage: 'Feed RSS do podcast' },
  feedHelp: {
    id: 'mpf_radio_feed_help',
    defaultMessage:
      'Endereço RSS do podcast. Com ele preenchido, dá para escolher os episódios de uma lista, com título, data, resumo e áudio já prontos.',
  },
  episodios: { id: 'mpf_radio_episodios', defaultMessage: 'Episódios' },
  episodiosHelp: {
    id: 'mpf_radio_episodios_help',
    defaultMessage:
      'Três por linha no computador. Episódio sem arquivo de áudio não aparece.',
  },
  episodio: { id: 'mpf_radio_episodio', defaultMessage: 'Episódio' },
  plataforma: { id: 'mpf_radio_plataforma', defaultMessage: 'Plataforma' },
  plataformaHelp: {
    id: 'mpf_radio_plataforma_help',
    defaultMessage: 'Marca exibida no alto do card, com link para o episódio.',
  },
  data: { id: 'mpf_radio_data', defaultMessage: 'Publicado em' },
  episodioTitulo: { id: 'mpf_radio_episodio_titulo', defaultMessage: 'Título' },
  descricao: { id: 'mpf_radio_descricao', defaultMessage: 'Resumo' },
  descricaoHelp: {
    id: 'mpf_radio_descricao_help',
    defaultMessage: 'Três linhas no card; o resto é cortado.',
  },
  audio: { id: 'mpf_radio_audio', defaultMessage: 'Arquivo de áudio' },
  audioHelp: {
    id: 'mpf_radio_audio_help',
    defaultMessage:
      'Um arquivo do site ou um endereço externo. É o que toca no card e o que o botão de baixar entrega.',
  },
  link: { id: 'mpf_radio_link', defaultMessage: 'Episódio na plataforma' },
  linkHelp: {
    id: 'mpf_radio_link_help',
    defaultMessage:
      'Destino da marca no alto do card e do botão de compartilhar. Em branco, compartilha a página.',
  },
});

/**
 * Campos de um episódio.
 *
 * `itemTitle` é o gancho da nossa customização do `ObjectListWidget`: numa
 * lista de episódios, "EPISÓDIO #3" não diz nada ao editor.
 */
const episodioItemSchema = ({ intl }: { intl: IntlShape }) => ({
  title: intl.formatMessage(messages.episodio),
  addMessage: intl.formatMessage(messages.episodio),
  itemTitle: (episodio: { titulo?: string }, index: number) =>
    episodio?.titulo?.trim() ||
    `${intl.formatMessage(messages.episodio)} #${index + 1}`,
  fieldsets: [
    {
      id: 'default',
      title: 'Default',
      fields: ['plataforma', 'data', 'titulo', 'descricao', 'audio', 'link'],
    },
  ],
  properties: {
    plataforma: {
      title: intl.formatMessage(messages.plataforma),
      description: intl.formatMessage(messages.plataformaHelp),
      // Os três tokens vêm de `Icons/social`, que já traz a geometria e a cor
      // de marca de cada uma — não há ícone novo a desenhar.
      choices: PLATAFORMAS.map((token) => [token, SOCIAL_LABELS[token]]),
    },
    data: {
      title: intl.formatMessage(messages.data),
      widget: 'datetime',
    },
    titulo: { title: intl.formatMessage(messages.episodioTitulo) },
    descricao: {
      title: intl.formatMessage(messages.descricao),
      description: intl.formatMessage(messages.descricaoHelp),
      widget: 'textarea',
    },
    audio: {
      title: intl.formatMessage(messages.audio),
      description: intl.formatMessage(messages.audioHelp),
      widget: 'object_browser',
      mode: 'link',
      selectedItemAttrs: ['@id', '@type', 'Title'],
      allowExternals: true,
      initialPath: '/',
    },
    link: {
      title: intl.formatMessage(messages.link),
      description: intl.formatMessage(messages.linkHelp),
      widget: 'object_browser',
      mode: 'link',
      selectedItemAttrs: ['@id', '@type', 'Title'],
      allowExternals: true,
      initialPath: '/',
    },
  },
  required: ['titulo', 'audio'],
});

/**
 * Schema do bloco "Rádio e podcast".
 *
 * `activeObject`/`setActiveObject` são o contrato do `ObjectListWidget` do
 * core — ver `Blocks/Videos/schema.ts`.
 */
export const mpfRadioSchema = ({
  intl,
  activeObject,
  setActiveObject,
}: {
  intl: IntlShape;
  activeObject?: number;
  setActiveObject?: (index: number) => void;
}) => ({
  title: intl.formatMessage(messages.titleBlock),
  fieldsets: [
    {
      id: 'default',
      title: 'Default',
      fields: ['titulo', 'exibirTitulo', 'feed', 'episodios'],
    },
  ],
  properties: {
    titulo: {
      title: intl.formatMessage(messages.titulo),
      description: intl.formatMessage(messages.tituloHelp, {
        padrao: RADIO_TITULO,
      }),
      placeholder: RADIO_TITULO,
    },
    exibirTitulo: {
      title: intl.formatMessage(messages.exibirTitulo),
      description: intl.formatMessage(messages.exibirTituloHelp),
      type: 'boolean',
      widget: 'toggle',
      default: false,
    },
    // O feed é a única fonte que entrega o ARQUIVO de áudio: nem o oEmbed do
    // Spotify nem a API do Deezer devolvem o MP3 (é o produto deles). O
    // `<enclosure>` do RSS, sim — ver `services/podcast_feed` no backend.
    feed: {
      title: intl.formatMessage(messages.feed),
      description: intl.formatMessage(messages.feedHelp),
      widget: 'url',
    },
    episodios: {
      title: intl.formatMessage(messages.episodios),
      description: intl.formatMessage(messages.episodiosHelp),
      widget: 'object_list',
      schema: episodioItemSchema({ intl }),
      activeObject,
      setActiveObject,
    },
  },
  required: [],
});

export default mpfRadioSchema;
